import './styles.css';

import React, { FunctionComponent, useState, useCallback, useMemo, useRef } from 'react';
import {
    Typography,
    // Button,
    LinkButton
} from '@vtblife/uikit';

import classname from '@search/classname/src';
import { parseAndFormatNumber } from '@search/helpers/src/formatNumber';
import { useUserDetailsLoader } from '@search/auth/src/userDetails/userDetails';
import { ApplicationIdEnum, MortgageOfferType } from '@search/graphql-typings/src';

import { MinimalBanksMortgageProps } from '../../../../desktop/components/MinimalBanksMortgage';
import {
    MinimalBanksMortgageOffer,
    useMinimalBanksMortgageOffersSliced
} from '../MinimalBanksMortgageOffers/MinimalBanksMortgageOffers';
import { offerRegionToMortgage } from '../../../../../domain/offer/regionToBackoffice';
import { useClassifiedOfferDataLayerPush } from '../../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { Mortgage2StarterModal } from '../mortgage2/starter/Mortgage2StarterModal';
import { Mortgage2ConsultModal } from '../mortgage2/consult/Mortgage2ConsultModal';
import { getGtmLabel } from '../../../../../domain/GtmContext';
import { MortgageOfferSkeleton } from '../../BanksMortgageOffers/MortgageOfferSkeleton';
import { BankMortgageOffers } from '../../BanksMortgageOffers';
import { MortgageGovernmentDisclaimer } from '../MortgageGovernmentDisclaimer/MortgageGovernmentDisclaimer';
import { MortgageGovernmentDisclaimerModal } from '../MortgageGovernmentDisclaimer/MortgageGovernmentDisclaimerModal';
import { MortgageBuyOrDieModal } from '../mortgageBuyOrDie/MortgageBuyOrDieModal';
import { MortgageBuyOrDieTrigger } from '../mortgageBuyOrDie/MortgageBuyOrDieTrigger';
import { MortgageStupidModal } from '../stupid/MortgageStupidModal';
import { mortgage2ParamsFromClasified } from '../mortgage2/mortgage2ParamsFromClasified';
import { useMortgage2Request } from '../mortgage2/useMortgage2Request';
import { defaultBanksTotal } from '../contants';
import { useGovernmentMortgage } from '../useGovernmentMortgage';
import { BanksMortgageCalculatorFields } from './MortgageCalculatorFields/MortgageCalculatorFields';
import { BanksMortgageOffersScroller } from './MortgageOffersScroller/MortgageOffersScroller';
import { MortgageDiscountConditions } from './MortgageDiscountConditions/MortgageDiscountConditions';
import { maxMortgageDiscountFormatted, useChangeDiscount, VTB_ID } from './useChangeDiscount';
import { NoOffersBanner } from './NoOffersBanner/NoOffersBanner';

const cn = classname.bind(null, 'MortgageCalculator');

// eslint-disable-next-line complexity
export const Mortgage3Calc : FunctionComponent<MinimalBanksMortgageProps> = React.memo(({
    term,
    isMobile,
    isPreferentialNbCondition,
    propertyCost,
    ownAmount,
    purpose,
    regionId,
    mortgageRegionId,
    areButtonsHidden,
    gtmStarterModalSendedId,
    gtmCommonClick,
    gtmSendClick,
    gtmShowMoreClick,
    gtmShowData,
    gtmSendData,
    applicationId,
    realtyType = 'ProfOfferFlat',
    withoutAuth,
    withoutForm,
    locationPlacement,
    buyOrDieMode,
    roomType,
    isNewBuilding = false,
    mortgageDemandLink,
    // eslint-disable-next-line react-hooks/rules-of-hooks
    mortgageOfferFormData = useMinimalBanksMortgageOffersSliced({
        term,
        propertyCost,
        ownAmount,
        purpose,
        isPreferentialNbCondition,
        regionId: regionId ? offerRegionToMortgage(regionId) : mortgageRegionId!,
        defaultVisibleOffers: 4
    }),
    minimalMortgagePaymentSendData,
    minimalMortgageRate
}) => {
    const [ senderState, setSenderState ] = useState({
        selectedMortgageOffer: undefined as MinimalBanksMortgageOffer | undefined,
        isSendModalOpened: false,
        isConsultModalOpened: false
    });
    const isPreferentialSelectedOffer = senderState.selectedMortgageOffer?.type === MortgageOfferType.Preferential || senderState.selectedMortgageOffer?.type === MortgageOfferType.Family;
    const [ isStupidModalOpened, setIsStupidModalOpened ] = useState(false);
    const [ isCalcBuyOrDieModalOpened, setIsCalcBuyOrDieModalOpened ] = useState(false);
    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const openBuyOrDieModal = useCallback(() => {
        setIsCalcBuyOrDieModalOpened(true);

        dataLayerPush({
            event: 'up_action',
            theme: 'ipoteka',
            page_type: 'card',
            type: 'buy_or_save_open'
        });
    }, [ dataLayerPush ]);
    const closeBuyOrDieModal = useCallback(() => setIsCalcBuyOrDieModalOpened(false));
    // чтобы обратили внимание на контекст вызова, если вызывать в другом - нужно сделать пропс про аналитику buy/die в модалке опциональным
    const openStupidModalByBuyOrDie = useCallback(() => setIsStupidModalOpened(true));
    const closeStupidModal = useCallback(() => setIsStupidModalOpened(false));

    const {
        isEmpty,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        loading,
        error,
        formData,
        setFormData,
        formUrl,
        offersFull,
        isFormChanged
    } = mortgageOfferFormData;

    const governmentMortgage = useGovernmentMortgage({
        ...formData,
        isPreferentialNbCondition,
    });
    const {
        offers,
        discount,
        onChangeDiscount,
        isShowAdditionalDiscounts,
        shouldVTBDiscountLogic,
        discountTypes,
    } = useChangeDiscount({
        ...formData,
        ...governmentMortgage,
        isPreferentialNbCondition,
        applicationId,
        offersFull
    });

    const minimalIncome = useMemo(() => {
        const firstNatureOfferMinimalPayment = offers.find(offer => ! offer?.disabled)?.minimalPayment;

        if (! firstNatureOfferMinimalPayment) return null;

        minimalMortgagePaymentSendData && minimalMortgagePaymentSendData(firstNatureOfferMinimalPayment);

        // Платеж не более 60% от дохода
        return parseAndFormatNumber(Math.round(firstNatureOfferMinimalPayment / 0.6));
    }, [ offers ]);

    const sendModalClose = useCallback(() => {
        setSenderState({ selectedMortgageOffer: undefined, isSendModalOpened: false, isConsultModalOpened: false });
    }, [ ]);

    const selectMortgageOffer = useCallback((
        selectedMortgageOffer: MinimalBanksMortgageOffer,
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        e.preventDefault();
        if (areButtonsHidden) return;
        if (selectedMortgageOffer?.type === 'PREFERENTIAL' || selectedMortgageOffer?.type === 'FAMILY') {
            dataLayerPush({
                event: 'up_action',
                theme: 'ipoteka',
                type: selectedMortgageOffer?.type === 'PREFERENTIAL' ? 'gospr_benefit_click' : 'gospr_family_click'
            });
        }
        if (withoutAuth) {
            gtmShowData && dataLayerPush({
                ...gtmShowData,
                place: 'bank',
            });
            setSenderState({ selectedMortgageOffer, isSendModalOpened: true, isConsultModalOpened: false });
        } else {
            domain.sendQuery(e);
        }
    }, [ areButtonsHidden, dataLayerPush, withoutAuth, gtmShowData ]);

    const consultModalOpen = useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
        e?.preventDefault();
        if (gtmShowMoreClick || gtmCommonClick) {
            dataLayerPush({ event: gtmShowMoreClick || gtmCommonClick });
        }
        gtmShowData && dataLayerPush({
            ...gtmShowData,
            place: 'consultation'
        });
        setSenderState({ selectedMortgageOffer: undefined, isSendModalOpened: false, isConsultModalOpened: true });
    }, [ gtmShowMoreClick, gtmCommonClick, dataLayerPush, gtmShowData ]);

    const consultModalClose = useCallback(() => {
        setSenderState({ selectedMortgageOffer: undefined, isSendModalOpened: false, isConsultModalOpened: false });
    }, [ ]);

    const willNeedSendDiscountsVTB = (
        senderState.selectedMortgageOffer?.bankId === VTB_ID ||
        (! senderState.selectedMortgageOffer && offers?.[0]?.bankId === VTB_ID)
    );

    // временное решение/дублирование.
    // Нужно переделать всё это на контексты внутри оффера, например.
    const params = mortgage2ParamsFromClasified({
        regionId,
        mortgageRegionId,
        purpose: formData.purpose,
        term: formData.term,
        ownAmount: formData.ownAmount,
        propertyCost: formData.propertyCost,
    });
    const banksTotal = senderState.selectedMortgageOffer ? defaultBanksTotal - 1 : defaultBanksTotal;

    const bestMortgageOffer = senderState.selectedMortgageOffer ?? mortgageOfferFormData.bestMortgageOffer;
    const initialBestMortgageOffer = useRef(bestMortgageOffer);

    if (! initialBestMortgageOffer.current && bestMortgageOffer) {
        initialBestMortgageOffer.current = bestMortgageOffer;
    }

    const userInfoLoad = useUserDetailsLoader();
    const userInfo = userInfoLoad({});
    const domain = useMortgage2Request({
        regionId,
        mortgageRegionId,
        purpose: formData.purpose,
        term: formData.term,
        ownAmount: params.ownAmount,
        propertyCost: params.propertyCost,
        locationPlacement,
        realtyType,
        applicationId,
        withoutAuth,
        withoutForm,
        shouldVTBDiscountLogic,
        showPreferential: governmentMortgage.showPreferential,
        gtmSendId: gtmStarterModalSendedId,
        discounts: willNeedSendDiscountsVTB ? discountTypes : undefined,
        userDetails: userInfo.data?.userProfile.person,
        banksTotal,
        selectedMortgageOffer: senderState.selectedMortgageOffer,
        bestMortgageOffer,
    });

    const [ isMrtgDisclaimerModalOpened, setIsMrtgDisclaimerModalOpened ] = React.useState(false);
    const handleSubmitRequest = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
        e?.preventDefault();
        setIsMrtgDisclaimerModalOpened(false);
        if (withoutAuth) {
            setSenderState({ selectedMortgageOffer: undefined, isSendModalOpened: true, isConsultModalOpened: false });
            gtmShowData && dataLayerPush({
                ...gtmShowData,
                place: senderState.selectedMortgageOffer ? 'bank' : gtmShowData.place
            });
        } else {
            domain.sendQuery(e);
        }
        if (applicationId === ApplicationIdEnum.ClassifiedDesktop || applicationId === ApplicationIdEnum.ClassifiedTouch) {
            dataLayerPush({
                event: 'classified_card_sale_ipoteka_calc_click'
            });
        }
    }, [ dataLayerPush, withoutAuth, gtmShowData, applicationId ]);

    if (error) return null;

    if (! isFormChanged && ! loading && offersFull.length === 0) {
        return null;
    }

    return (
        <>
            <form
                method='GET'
                target='_blank'
                action={formUrl}
                className={cn()}
            >
                <Typography variant='promo-h3'>
                    {governmentMortgage.showPreferential ?
                        <>Доступные ипотечные программы</> :
                        <>Ипотека с&nbsp;экономией до&nbsp;{maxMortgageDiscountFormatted}% на&nbsp;ставке</>
                    }
                </Typography>
                <BanksMortgageCalculatorFields
                    className={cn('fields')}
                    term={formData.term}
                    purpose={formData.purpose}
                    regionId={formData.regionId}
                    ownAmount={formData.ownAmount}
                    propertyCost={formData.propertyCost}
                    onChange={setFormData}
                />
                {offers.length && minimalIncome ? <Typography bold>
                    {`Доход заемщика не менее: ${minimalIncome} ₽/мес.`}
                </Typography> : <>&nbsp;</>}
                <div className={cn('scroller-wrap')}>
                    <BanksMortgageOffersScroller isControls={offersFull.length > 4}>
                        {loading ? (
                            [ undefined, undefined, undefined, undefined, undefined ].map((item, index) => (
                                <MortgageOfferSkeleton key={index} />
                            ))
                        ) : null}
                        {isEmpty ? <NoOffersBanner /> : null}
                        {offers.length && ! loading ? (
                            offers.map((offer, index) => (
                                <BankMortgageOffers
                                    offer={offer}
                                    key={String(offer?.type ?? 'unk') + '' + (offer?.bankId ?? String(index))}
                                    gtmCommonClick={gtmCommonClick}
                                    selectMortgageOffer={selectMortgageOffer}
                                />
                            ))
                        ) : null}
                    </BanksMortgageOffersScroller>
                </div>
                {governmentMortgage.showPreferential && (<div className={cn('conditions')}>
                    {isMrtgDisclaimerModalOpened && (<MortgageGovernmentDisclaimerModal
                        btnText={`${withoutAuth ? 'Подать' : 'Оформить'} заявку на ипотеку`}
                        isMobile={isMobile}
                        onSuccess={handleSubmitRequest}
                        onClose={() => {
                            setIsMrtgDisclaimerModalOpened(false);
                        }}
                    />)}
                    <MortgageGovernmentDisclaimer
                        isLongDescr
                        isMobile={isMobile}
                        onClick={() => {
                            setIsMrtgDisclaimerModalOpened(true);
                        }}
                    />
                </div>)}
                {isShowAdditionalDiscounts && ! buyOrDieMode && (<MortgageDiscountConditions
                    className={cn('conditions')}
                    discount={discount.toFixed(1)}
                    discountTypes={discountTypes}
                    onChangeDiscount={onChangeDiscount}
                />)}
                {buyOrDieMode && bestMortgageOffer && (
                    <MortgageBuyOrDieTrigger className={cn('buyOrAcc')} onClick={openBuyOrDieModal} />
                )}
                <div className={cn('buttons')}>
                    <LinkButton
                        variant='accent'
                        type='submit'
                        onClick={mortgageDemandLink ? undefined : handleSubmitRequest}
                        href={mortgageDemandLink}
                        {...getGtmLabel(gtmSendClick || gtmCommonClick)}
                        disabled={loading || isEmpty}
                        fullWidth
                        width={buyOrDieMode ? 292 : undefined}
                        dataTest='MinimalBanksMortgage-send'
                    >
                        {`${withoutAuth ? 'Подать' : 'Оформить'} заявку${buyOrDieMode ? ' на ипотеку' : ''}`}
                    </LinkButton>
                    {/*{! buyOrDieMode && (*/}
                    {/*    <Button*/}
                    {/*        dataTest='MinimalBanksMortgage-show-more'*/}
                    {/*        type='submit'*/}
                    {/*        onClick={consultModalOpen}*/}
                    {/*        variant='transparent'*/}
                    {/*    >*/}
                    {/*        Получить консультацию*/}
                    {/*    </Button>*/}
                    {/*)}*/}
                </div>
                {senderState.isSendModalOpened ? <Mortgage2StarterModal
                    governmentMortgage={governmentMortgage}
                    withoutAuth={withoutAuth}
                    locationPlacement={locationPlacement}
                    gtmSendId={gtmStarterModalSendedId}
                    fields={isPreferentialSelectedOffer ? [ 'rate', 'payment' ] : undefined}
                    onClose={sendModalClose}
                    selectedMortgageOffer={senderState.selectedMortgageOffer}
                    mortgageOffers={offers}
                    realtyType={realtyType}
                    regionId={regionId}
                    mortgageRegionId={mortgageRegionId}
                    applicationId={applicationId}
                    purpose={formData.purpose}
                    term={formData.term}
                    ownAmount={formData.ownAmount}
                    propertyCost={formData.propertyCost}
                    shouldVTBDiscountLogic={shouldVTBDiscountLogic}
                    discounts={willNeedSendDiscountsVTB ? discountTypes : undefined}
                    gtmShowData={gtmShowData}
                    gtmSendData={gtmSendData}
                /> : null}
                {senderState.isConsultModalOpened ? <Mortgage2ConsultModal
                    regionId={regionId}
                    realtyType={realtyType}
                    applicationId={applicationId}
                    locationPlacement={locationPlacement}
                    gtmSendData={gtmSendData}
                    // always sending if VTB is present in offers (not possible to choose a bank)
                    discounts={isShowAdditionalDiscounts ? discountTypes : undefined}
                    onClose={consultModalClose}
                /> : null}
                {isCalcBuyOrDieModalOpened && initialBestMortgageOffer.current && (
                    <MortgageBuyOrDieModal
                        regionId={regionId}
                        mortgagePercent={initialBestMortgageOffer.current.minimalPercent}
                        roomType={roomType}
                        propertyCost={propertyCost}
                        onSuccess={openStupidModalByBuyOrDie}
                        onClose={closeBuyOrDieModal}
                    />
                )}
                {isStupidModalOpened && (
                    <MortgageStupidModal
                        onClose={closeStupidModal}
                        applicationId={applicationId}
                        gtmSendId={gtmStarterModalSendedId}
                        regionId={regionId}
                        locationPlacement={locationPlacement}
                        isNewBuilding={isNewBuilding}
                        propertyCost={propertyCost}
                        realtyType={realtyType}
                        isSendBuyDieAnalytics
                        minimalMortgageRate={minimalMortgageRate}
                    />
                )}
            </form>
            <Typography variant='secondary-alone' color='black-400' className={cn('mortgage-disclaimer')}>
                Расчёт условий не&nbsp;является публичной офертой. Финальные условия кредитования определяются при заключении договора
            </Typography>
        </>
    );
});
